import { Box, Flex, Text, Image } from "@chakra-ui/react";
import { t } from "i18next";
import type { GoldMonthlyTitle } from "../types";
import { getTitleCoverUrl, getTitleName, getBundleItemsForStack, isTitleClaimed } from "../utils";
import { BundleImageStack } from "../../../components/cards/components/BundleImageStack";
import { CheckIcon } from "../../../components/icons/CheckIcon";

// Figma: https://www.figma.com/design/z9Ut4lxLJQZK28vEZiHRwN/Monthly-rewards?node-id=122-3941

const TYPE_BADGE = {
  release: { bg: "transparent.error.80" },
  bundle: { bg: "transparent.success.80" },
};

export function CoverArea({
  titleData,
  onClick,
}: {
  titleData: GoldMonthlyTitle;
  onClick?: () => void;
}) {
  const isBundle = titleData.type === "bundle";
  const displayTitle = getTitleName(titleData);
  const bundleItems = isBundle ? getBundleItemsForStack(titleData) : [];
  const bundleItemCount = titleData.bundle?.items?.length ?? 0;
  const badge = isBundle ? TYPE_BADGE.bundle : TYPE_BADGE.release;
  const isClaimed = isTitleClaimed(titleData);
  const isClickable = !!onClick;

  return (
    <Box
      position="relative"
      borderRadius="12px"
      overflow="hidden"
      // Create a new stacking context so the inner stacked-image z-indexes
      // stay contained and can't bleed above modals / slide-in menus.
      sx={{ isolation: "isolate" }}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      cursor={isClickable ? "pointer" : undefined}
      aria-label={isClickable ? t("screens.monthlyComics.openDetailsAria", { title: displayTitle }) : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      _focusVisible={isClickable ? { outline: "2px solid", outlineColor: "blaze.blaze", outlineOffset: "2px" } : undefined}
    >
      {isBundle ? (
        <Box w="100%" aspectRatio="250/386" position="relative">
          <BundleImageStack items={bundleItems} layout="portrait" totalBundleItems={bundleItemCount} disableHover claimedOverlay={isClaimed} />
        </Box>
      ) : (
        <Image
          src={getTitleCoverUrl(titleData)}
          alt={displayTitle}
          w="100%"
          aspectRatio="250/386"
          objectFit="cover"
          display="block"
          fallback={<Box w="100%" aspectRatio="250/386" bg="whiteAlpha.100" />}
        />
      )}
      {/* Border overlay — skip for bundles since the stacked images have their own borders */}
      {!isBundle && (
        <Box
          position="absolute"
          inset={0}
          borderRadius="12px"
          border="1px solid"
          borderColor="whiteAlpha.100"
          pointerEvents="none"
        />
      )}
      {/* Type badge — bottom left (same offset for release and bundle).
          zIndex 30 keeps it above the claimed-state dark overlay (z 20) so
          the Release/Bundle pill stays readable when the cover is dimmed. */}
      <Flex
        position="absolute"
        bottom="12px"
        left="12px"
        align="center"
        gap="8px"
        zIndex={30}
      >
        <Flex
          align="center"
          h="29px"
          px="9px"
          py="5px"
          bg={badge.bg}
          borderRadius="full"
          border="1px solid"
          borderColor="whiteAlpha.200"
          backdropFilter="blur(8px)"
          flexShrink={0}
        >
          <Text fontSize="14px" fontWeight={600} color="whiteAlpha.800" whiteSpace="nowrap" lineHeight="20px">
            {isBundle ? t("screens.monthlyComics.typeBundleCount", { count: bundleItemCount }) : t("screens.monthlyComics.typeRelease")}
          </Text>
        </Flex>
      </Flex>
      {/* Claimed checkmark overlay. For bundles the dim is applied per-image
          inside BundleImageStack (so only the cover silhouettes darken, not
          the rectangular container around the staggered stack). For single
          releases we dim the container here. */}
      {isClaimed && (
        <Flex
          position="absolute"
          inset={0}
          align="center"
          justify="center"
          pointerEvents="none"
          bg={isBundle ? undefined : "blackAlpha.500"}
          zIndex={20}
        >
          <Flex
            bg="success.500"
            borderRadius="full"
            boxSize="60px"
            align="center"
            justify="center"
          >
            <CheckIcon boxSize="36px" color="white" />
          </Flex>
        </Flex>
      )}
    </Box>
  );
}
